import React, { useState } from 'react';
import { Card } from './ui/Card';
import { GraduationCap, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface CoachingItem {
  title: string;
  priority: 'Critical' | 'Important' | 'Suggested';
  whatWasMissed: string;
  whyItMatters: string;
  howToImprove: string;
}
const coachingItems: CoachingItem[] = [{
  title: 'Concrete Terms',
  priority: 'Critical',
  whatWasMissed: 'Discuss concrete terms and conditions relevant to debt consolidation.',
  whyItMatters: "Without specific terms, the client can't make an informed decision and may disengage or seek competitors who provide clearer information.",
  howToImprove: 'Try saying: "Let me walk you through the specific rates and terms for debt consolidation through a HELOC — currently we\'re seeing X% for qualified borrowers like yourself."'
}, {
  title: 'Full Application',
  priority: 'Important',
  whatWasMissed: 'Avoid disqualifying the client prior to taking a full application.',
  whyItMatters: 'Pre-qualifying without a full application risks losing viable borrowers who may appear borderline on surface-level criteria.',
  howToImprove: 'Instead of assessing eligibility verbally, say: "Let\'s get your full application started — it only takes 10 minutes and gives us the complete picture to find you the best option."'
}, {
  title: 'Thorough Financial Assessment',
  priority: 'Important',
  whatWasMissed: "Collect and assess the customer's financial information thoroughly.",
  whyItMatters: 'Incomplete financial picture leads to mismatched product recommendations, reducing close rates and client satisfaction.',
  howToImprove: 'Ask: "Beyond the mortgage, can you walk me through your monthly obligations? This helps me ensure we\'re recommending the right product and payment structure."'
}];
const priorityStyles = {
  Critical: 'bg-red-50 text-red-700 border-red-200',
  Important: 'bg-amber-50 text-amber-700 border-amber-200',
  Suggested: 'bg-blue-50 text-blue-700 border-blue-200'
};
export function MissedItemsCard({
  highlighted
}: {
  highlighted?: boolean;
}) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  return <Card delay={0.5} highlighted={highlighted} data-id="element-151">
      <h2 className="text-2xl text-navy flex items-center gap-2 mb-6" data-id="element-152">
        <GraduationCap className="text-crimson" size={24} data-id="element-153" />
        Call Coaching
      </h2>

      <div className="space-y-4" data-id="element-154">
        {coachingItems.map((item, i) => {
        const isExpanded = expandedIndex === i;
        return <div key={i} className="border border-warmgray rounded-lg overflow-hidden" data-id="element-155">
              {/* Header */}
              <div className="p-4" data-id="element-156">
                <div className="flex items-center gap-2 mb-2" data-id="element-157">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${priorityStyles[item.priority]}`} data-id="element-158">
                    {item.priority}
                  </span>
                  <h4 className="font-medium text-sm text-gray-900" data-id="element-159">
                    {item.title}
                  </h4>
                </div>

                <div className="mb-2" data-id="element-160">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1" data-id="element-161">
                    What was missed
                  </p>
                  <p className="text-sm text-gray-700" data-id="element-162">{item.whatWasMissed}</p>
                </div>

                <div className="mb-3" data-id="element-163">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1" data-id="element-164">
                    Why it matters
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-165">{item.whyItMatters}</p>
                </div>

                <button onClick={() => setExpandedIndex(isExpanded ? null : i)} className="flex items-center gap-1 text-xs font-medium text-crimson hover:text-crimson-dark transition-colors cursor-pointer" data-id="element-166">
                  <motion.span animate={{
                rotate: isExpanded ? 180 : 0
              }} transition={{
                duration: 0.2
              }} className="inline-flex" data-id="element-167">
                    <ChevronDown size={14} data-id="element-168" />
                  </motion.span>
                  {isExpanded ? 'Hide coaching tip' : 'Show coaching tip'}
                </button>
              </div>

              {/* Expandable Coaching Tip */}
              <AnimatePresence data-id="element-169">
                {isExpanded && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.25,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-170">
                    <div className="px-4 pb-4 pt-2 bg-emerald-50/60 border-t border-emerald-100" data-id="element-171">
                      <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-2" data-id="element-172">
                        How to improve
                      </p>
                      <p className="text-sm text-emerald-900/80 leading-relaxed italic" data-id="element-173">
                        {item.howToImprove}
                      </p>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>;
      })}
      </div>
    </Card>;
}